import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import CreatingPanel from './CreatingPanel';
import ErrorPanel from './ErrorPanel';
import StepHeader from './StepHeader';
import SuccessPanel from './SuccessPanel';
import { CREATING_PANEL, ERROR_PANEL, SUCCESS_PANEL } from './copy';

const USER = { username: 'jane.doe', id: 42 };

const wrap = (node) => render(<ConfigProvider>{node}</ConfigProvider>);

describe('management/Users CreateUserWizard panels', () => {
  describe('StepHeader', () => {
    it('shows the title and guidance', () => {
      wrap(<StepHeader subtitle="Do the thing." title="A Step" />);

      expect(screen.getByText('A Step')).toBeInTheDocument();
      expect(screen.getByText('Do the thing.')).toBeInTheDocument();
    });

    it('omits the required-fields key by default', () => {
      wrap(<StepHeader subtitle="Do the thing." title="A Step" />);

      expect(screen.queryByText('Required Fields')).not.toBeInTheDocument();
    });

    it('shows the key when asked', () => {
      wrap(<StepHeader showRequiredKey subtitle="Do it." title="A Step" />);

      expect(screen.getByText('Required Fields')).toBeInTheDocument();
    });
  });

  describe('CreatingPanel', () => {
    it('says what is happening and that closing is safe, for a plain create', () => {
      wrap(<CreatingPanel />);

      expect(
        screen.getByText(CREATING_PANEL.created.title),
      ).toBeInTheDocument();
      expect(screen.getByText(CREATING_PANEL.created.body)).toBeInTheDocument();
    });

    // There are no Teams in PD-4064, so the mockup's "Applying teams, accounts,
    // and permissions" would promise work that never happens.
    it('does not claim to be applying teams', () => {
      wrap(<CreatingPanel />);

      expect(screen.queryByText(/teams/i)).not.toBeInTheDocument();
    });

    it.each([
      ['restored', CREATING_PANEL.restored],
      ['added', CREATING_PANEL.added],
      ['updated', CREATING_PANEL.updated],
    ])('shows %s-specific copy for that kind', (kind, copy) => {
      wrap(<CreatingPanel kind={kind} />);

      expect(screen.getByText(copy.title)).toBeInTheDocument();
      expect(screen.getByText(copy.body)).toBeInTheDocument();
    });
  });

  describe('SuccessPanel', () => {
    it('names the username the server derived, for a plain create', () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(screen.getByText(/jane\.doe/)).toBeInTheDocument();
      expect(screen.getByText(SUCCESS_PANEL.created.title)).toBeInTheDocument();
    });

    // Required by the ticket: the admin never sets a password.
    it('says a welcome email goes out for a plain create', () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(
        screen.getByText(new RegExp(SUCCESS_PANEL.created.emailNotice)),
      ).toBeInTheDocument();
    });

    it.each(['restored', 'added', 'updated'])(
      'says nothing about a welcome email for the %s outcome',
      (kind) => {
        wrap(
          <SuccessPanel kind={kind} user={USER} onCreateAnother={() => {}} />,
        );

        expect(screen.queryByText(/welcome email/i)).not.toBeInTheDocument();
        expect(screen.getByText(SUCCESS_PANEL[kind].title)).toBeInTheDocument();
      },
    );

    // PD-4596. A real anchor, not the greyed span a disabled Link renders.
    it("links View New User to the new user's edit page", () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(
        screen.getByRole('link', { name: new RegExp(SUCCESS_PANEL.viewUser) }),
      ).toHaveAttribute('href', '/management/users/42/edit');
    });

    // The wizard re-renders around this panel, and KeyDS `Link` dies in
    // areHookInputsEqual when it does. See PlainLink.
    it('survives a re-render', () => {
      const { rerender } = wrap(
        <SuccessPanel user={USER} onCreateAnother={() => {}} />,
      );

      expect(() =>
        rerender(
          <ConfigProvider>
            <SuccessPanel user={USER} onCreateAnother={() => {}} />
          </ConfigProvider>,
        ),
      ).not.toThrow();

      expect(
        screen.getByRole('link', { name: new RegExp(SUCCESS_PANEL.viewUser) }),
      ).toHaveAttribute('href', '/management/users/42/edit');
    });

    // The legacy form drops the admin out of the tool they are working in.
    it('does not send the admin to the legacy user form', () => {
      wrap(<SuccessPanel user={USER} onCreateAnother={() => {}} />);

      expect(
        document.querySelector('[href="/users/edit/42"]'),
      ).not.toBeInTheDocument();
    });

    it('offers to create another', () => {
      const onCreateAnother = jest.fn();
      wrap(<SuccessPanel user={USER} onCreateAnother={onCreateAnother} />);

      fireEvent.click(
        screen.getByRole('button', {
          name: new RegExp(SUCCESS_PANEL.createAnother, 'i'),
        }),
      );

      expect(onCreateAnother).toHaveBeenCalledTimes(1);
    });

    it('names a skipped account when resolveExisting dropped one', () => {
      wrap(
        <SuccessPanel
          kind="added"
          skippedCount={1}
          user={USER}
          onCreateAnother={() => {}}
        />,
      );

      expect(
        screen.getByText(SUCCESS_PANEL.partialCaveat(1), { exact: false }),
      ).toBeInTheDocument();
    });

    it('pluralizes the skipped-account caveat for more than one', () => {
      wrap(
        <SuccessPanel
          kind="added"
          skippedCount={2}
          user={USER}
          onCreateAnother={() => {}}
        />,
      );

      expect(
        screen.getByText(SUCCESS_PANEL.partialCaveat(2), { exact: false }),
      ).toBeInTheDocument();
    });

    it('says nothing about skipped accounts when none were', () => {
      wrap(
        <SuccessPanel kind="added" user={USER} onCreateAnother={() => {}} />,
      );

      expect(screen.queryByText(/skipped/i)).not.toBeInTheDocument();
    });
  });

  describe('ErrorPanel', () => {
    // Deliberately generic, for every one of the wizard's outcomes alike:
    // field-level problems are caught on their own step, so anything reaching
    // here is unexpected and the details are in the server log.
    it('reports the failure without detail', () => {
      wrap(<ErrorPanel onRetry={() => {}} />);

      expect(screen.getByText(ERROR_PANEL.title)).toBeInTheDocument();
      expect(screen.getByText(ERROR_PANEL.body)).toBeInTheDocument();
    });

    it('offers a retry', () => {
      const onRetry = jest.fn();
      wrap(<ErrorPanel onRetry={onRetry} />);

      fireEvent.click(
        screen.getByRole('button', {
          name: new RegExp(ERROR_PANEL.retry, 'i'),
        }),
      );

      expect(onRetry).toHaveBeenCalledTimes(1);
    });

    it('shows the body override when given one', () => {
      wrap(
        <ErrorPanel
          body="Your own account access cannot be changed here."
          onRetry={() => {}}
        />,
      );

      expect(
        screen.getByText('Your own account access cannot be changed here.'),
      ).toBeInTheDocument();
      expect(screen.queryByText(ERROR_PANEL.body)).not.toBeInTheDocument();
    });
  });
});
